/**
 * Per-user access flags stored on `users/{id}.permissions` (string[]) and mirrored in Auth custom claims.
 * Admin / Administrator role always has full access (ignores stored `permissions` so admins are never locked out).
 * Otherwise, if `permissions` is a stored array (including `[]`), that list is used (valid IDs only).
 * If missing/undefined, {@link defaultPermissionsForRole}.
 */

export const PERMISSION_IDS = [
  "dashboard",
  "change_password",
  "personal",
  "billing",
  "purchase",
  "reports",
  "monthly_sale_report",
  "yearly_sale_report",
  "monthly_incentive",
  "party_creation",
  "masters",
  "transactions",
  "purchase_orders",
  "pending_orders",
  "payroll",
  "tools",
  "accounting",
  "admin",
  "customer_entry",
  "order_reports",
  "movement_analysis",
  "sales_ageing",
  "profit_loss",
  "gstr1",
  "cash_book",
  "bank_book",
  "credit_due",
  "cctv",
  "cctv_admin",
  "bi_reports",
];

/** @type {Record<string, { label: string, group: string; hint?: string }>} */
export const PERMISSION_LABELS = {
  dashboard: {
    label: "Dashboard",
    group: "General",
    hint: "/dashboard",
  },
  change_password: {
    label: "Change my password",
    group: "General",
    hint: "/change-password (File menu)",
  },
  personal: {
    label: "Personal data",
    group: "General",
    hint: "/personal (Personal menu — upload / download / edit)",
  },
  billing: {
    label: "Outward Supply",
    group: "Trading",
    hint: "/billing, /print-bill",
  },
  purchase: { label: "Inward Supply", group: "Trading", hint: "/voucher/inward-supply, /purchase" },
  reports: {
    label: "Reports hub & stock report",
    group: "Reports",
    hint: "/reports/*, /stock-report, /movement-analysis, /stock-forecast",
  },
  monthly_sale_report: {
    label: "Monthly sale report (import)",
    group: "Reports",
    hint: "/reports/monthly-sale-report-import, /reports/monthly-sale-report-import/generate",
  },
  yearly_sale_report: {
    label: "Yearly sale report",
    group: "Reports",
    hint: "/reports/yearly-sale-report",
  },
  monthly_incentive: {
    label: "Monthly sale incentive page",
    group: "Reports",
    hint: "/reports/monthly-incentive",
  },
  party_creation: {
    label: "Party / ledger creation",
    group: "Masters",
    hint: "/party-creation, /ledger/:id",
  },
  masters: {
    label: "Masters (products, GST, HSN, brands, party discounts, credit/due data)",
    group: "Masters",
    hint: "/add-product, /group-master, … /party-Discounts, /credit-due-master",
  },
  transactions: {
    label: "Trading / view stock",
    group: "Operations",
    hint: "/monthly-product-report (View stock)",
  },
  purchase_orders: { label: "Purchase orders", group: "Operations", hint: "/purchase-orders" },
  pending_orders: { label: "Pending orders", group: "Operations", hint: "/pending-orders" },
  payroll: {
    label: "Payroll & HR",
    group: "HR",
    hint:
      "Monitor /payroll (attendance) · Monthly report /payroll/monthly-report (absent) · daily hours, holidays, employees, reports…",
  },
  tools: { label: "Tools (add item, Excel import)", group: "Tools", hint: "/excel-import" },
  accounting: {
    label: "Accounting (ledger popup, books)",
    group: "Accounting",
    hint: "Ledger popup, AccountBooks submenu",
  },
  admin: { label: "Admin panel", group: "Admin", hint: "/admin/*" },
  customer_entry: { label: "Customer entry report", group: "Reports", hint: "/reports/customer-entry" },
  order_reports: { label: "Order reports", group: "Reports", hint: "/reports/order-reports" },
  movement_analysis: { label: "Movement analysis", group: "Reports", hint: "/movement-analysis" },
  sales_ageing: { label: "Sales ageing", group: "Reports", hint: "/party-ageing-report" },
  profit_loss: { label: "Profit & loss", group: "Reports", hint: "/profit-loss" },
  bi_reports: { label: "Business Intelligence", group: "Reports", hint: "/bi, /bi/*" },
  gstr1: { label: "GSTR-1 / GST tools", group: "Reports", hint: "/gstr1" },
  cash_book: { label: "Cash book", group: "Accounting", hint: "/cash-book" },
  bank_book: { label: "Bank book", group: "Accounting", hint: "/bank-book" },
  credit_due: {
    label: "Credit / Due (Udhar)",
    group: "Operations",
    hint: "/credit-due",
  },
  cctv: {
    label: "CCTV live view",
    group: "Security",
    hint: "/cctv",
  },
  cctv_admin: {
    label: "CCTV administration",
    group: "Security",
    hint: "/cctv/cameras, /tools/cctv-settings",
  },
};

const ALL = [...PERMISSION_IDS];
/** Backward-compat permission IDs that should map to current IDs. */
const LEGACY_PERMISSION_ALIASES = {
  monthly_sale_incentive: "monthly_incentive",
  monthly_sale_incentive_page: "monthly_incentive",
};

/** Admin / Administrator (e.g. Flutter or legacy consoles) get full access */
export function isAdminRole(role) {
  const r = String(role || "").toLowerCase();
  return r === "admin" || r === "administrator";
}

/** Defaults mirror the previous role-only behaviour */
export function defaultPermissionsForRole(role) {
  const r = String(role || "viewer").toLowerCase();
  if (r === "admin" || r === "administrator") return [...ALL];
  if (r === "staff") {
    return [
      "dashboard",
      "change_password",
      "personal",
      "billing",
      "purchase",
      "reports",
      "bi_reports",
      "monthly_sale_report",
      "yearly_sale_report",
      "monthly_incentive",
      "party_creation",
      "masters",
      "transactions",
      "purchase_orders",
      "pending_orders",
      "tools",
      "customer_entry",
      "order_reports",
      "movement_analysis",
      "sales_ageing",
      "profit_loss",
      "gstr1",
      "cash_book",
      "bank_book",
      "credit_due",
    ];
  }
  if (r === "accounts") {
    return [
      "dashboard",
      "change_password",
      "personal",
      "billing",
      "purchase",
      "reports",
      "bi_reports",
      "monthly_sale_report",
      "yearly_sale_report",
      "monthly_incentive",
      "party_creation",
      "payroll",
      "accounting",
      "customer_entry",
      "order_reports",
      "movement_analysis",
      "sales_ageing",
      "profit_loss",
      "gstr1",
      "cash_book",
      "bank_book",
      "credit_due",
    ];
  }
  if (r === "viewer") {
    return [
      "dashboard",
      "change_password",
      "personal",
      "reports",
      "bi_reports",
      "monthly_sale_report",
      "yearly_sale_report",
      "monthly_incentive",
      "customer_entry",
      "order_reports",
      "movement_analysis",
      "sales_ageing",
      "profit_loss",
      "gstr1",
    ];
  }
  return ["dashboard", "change_password", "personal", "reports"];
}

/**
 * @param {{ role?: string, permissions?: unknown }} user — typically `JSON.parse(localStorage.user)`
 * @returns {string[]}
 */
export function getEffectivePermissions(user) {
  if (isAdminRole(user?.role)) {
    return [...PERMISSION_IDS];
  }
  const raw = user?.permissions;
  if (Array.isArray(raw)) {
    const allowed = new Set(PERMISSION_IDS);
    const normalize = (p) => String(p || "").trim().toLowerCase();
    const mapped = raw
      .filter((p) => typeof p === "string")
      .map((p) => {
        const norm = normalize(p);
        return LEGACY_PERMISSION_ALIASES[norm] || norm;
      });
    return [...new Set(mapped.filter((p) => allowed.has(p)))];
  }
  return defaultPermissionsForRole(user?.role);
}

/**
 * @param {{ role?: string, permissions?: unknown }} user
 * @param {string} permId
 */
export function hasPermission(user, permId) {
  if (!permId) return true;
  return getEffectivePermissions(user).includes(permId);
}

/**
 * @param {{ role?: string, permissions?: unknown }} user
 * @param {string[]} anyOf
 */
export function hasAnyPermission(user, anyOf) {
  if (!anyOf?.length) return true;
  const set = new Set(getEffectivePermissions(user));
  return anyOf.some((p) => set.has(p));
}
